/**
 * EVENTSFLO-FINANCIAL-TAX-RULES-LINE-LEVEL-AUDIT-VIEW-V1
 *
 * Read-only popover exposing the snapshotted tax decision for a
 * quote/invoice line. The popover ONLY displays stored snapshot
 * values — no recalculation, no re-evaluation of the current tax
 * rules matrix. Older lines that pre-date snapshotting show
 * "Not captured" gracefully.
 */
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Badge } from "../ui/badge";
import { Info, AlertTriangle } from "lucide-react";
import { cn } from "../../lib/utils";

const NC = <span className="italic text-muted-foreground/70">Not captured</span>;

function formatPct(value) {
  if (value === undefined || value === null || value === "") return NC;
  const n = Number(value);
  if (Number.isNaN(n)) return NC;
  const scaled = n <= 1 ? n * 100 : n;
  return `${scaled.toFixed(2).replace(/\.00$/, "")}%`;
}

function fmt(value) {
  if (value === undefined || value === null || value === "") return NC;
  return String(value);
}

function fmtCurrency(value, symbol = "€") {
  if (value === undefined || value === null || value === "") return NC;
  const n = Number(value);
  if (Number.isNaN(n)) return NC;
  return `${symbol}${n.toFixed(2)}`;
}

function Row({ label, value, testId }) {
  return (
    <div className="grid grid-cols-[130px_1fr] gap-2 text-xs">
      <div className="text-muted-foreground">{label}</div>
      <div className="font-mono break-words" data-testid={testId}>
        {value}
      </div>
    </div>
  );
}

/**
 * @param {{line: object, lineId?: string, currencySymbol?: string}} props
 */
export function TaxDecisionAuditPopover({ line, lineId, currencySymbol = "€" }) {
  if (!line) return null;

  const {
    tax_class_snapshot,
    client_tax_treatment_snapshot,
    tax_rule_id_snapshot,
    vat_rate_snapshot,
    vat_amount_snapshot,
    vat_label_snapshot,
    reverse_charge_applied_snapshot,
    tax_reason_snapshot,
    invoice_note_snapshot,
    requires_manual_review_snapshot,
    // Fallbacks from legacy pre-snapshot lines:
    tax_rate,
    tax_amount,
    tax_mode,
  } = line;

  const id = lineId
    || line.id
    || line.line_id
    || line.task_id
    || line.description
    || "unknown";

  const isRC = !!reverse_charge_applied_snapshot;
  const isManualReview = !!requires_manual_review_snapshot;
  const label = vat_label_snapshot
    || (tax_mode === "exempt" ? "VAT 0% (exempt)"
      : tax_mode === "out_of_scope" ? "Out of scope"
      : (tax_rate != null ? `TVA ${formatPct(tax_rate)}` : null));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`tax-decision-audit-trigger-${id}`}
          className={cn(
            "inline-flex items-center gap-1 px-1.5 py-0.5 ml-2 rounded",
            "border text-[10px] font-medium transition-colors align-middle",
            isRC
              ? "border-amber-500/60 text-amber-600 hover:bg-amber-500/10"
              : isManualReview
                ? "border-orange-500/60 text-orange-600 hover:bg-orange-500/10"
                : "border-slate-400/40 text-muted-foreground hover:bg-muted",
          )}
          aria-label="View tax decision for this line"
        >
          {label ? (
            <span>{label}</span>
          ) : (
            <>
              <Info className="h-3 w-3" />
              <span>Tax</span>
            </>
          )}
          {isManualReview && <AlertTriangle className="h-3 w-3 ml-0.5" />}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-96"
        side="top"
        align="end"
        data-testid="tax-decision-audit-panel"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold">Tax decision (snapshot)</div>
          {isManualReview && (
            <Badge
              variant="outline"
              className="border-orange-500/60 text-orange-600"
              data-testid="tax-decision-manual-review"
            >
              <AlertTriangle className="h-3 w-3 mr-1" />
              Manual review
            </Badge>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground mb-3 leading-snug">
          This shows the tax decision captured at document generation time.
          It may differ from the current Tax Rules Matrix if rules were
          edited afterwards.
        </p>
        <div className="space-y-1.5">
          <Row
            label="Line tax class"
            value={fmt(tax_class_snapshot)}
            testId="tax-decision-tax-class"
          />
          <Row
            label="Client treatment"
            value={fmt(client_tax_treatment_snapshot)}
            testId="tax-decision-client-treatment"
          />
          <Row
            label="Matched rule"
            value={fmt(tax_rule_id_snapshot)}
            testId="tax-decision-rule-id"
          />
          <Row
            label="VAT rate"
            value={formatPct(vat_rate_snapshot ?? tax_rate)}
            testId="tax-decision-vat-rate"
          />
          <Row
            label="VAT amount"
            value={fmtCurrency(vat_amount_snapshot ?? tax_amount, currencySymbol)}
            testId="tax-decision-vat-amount"
          />
          <Row
            label="VAT label"
            value={fmt(vat_label_snapshot)}
          />
          <Row
            label="Reverse charge"
            value={
              reverse_charge_applied_snapshot === undefined
                ? NC
                : reverse_charge_applied_snapshot ? "Yes" : "No"
            }
          />
          <Row
            label="Reason"
            value={fmt(tax_reason_snapshot)}
            testId="tax-decision-reason"
          />
          {invoice_note_snapshot && (
            <div className="border-t border-white/10 mt-2 pt-2">
              <div className="text-[11px] text-muted-foreground italic">
                {invoice_note_snapshot}
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default TaxDecisionAuditPopover;
